import React from 'react';
import dynamic from 'next/dynamic';
import { DynamicComponentObject } from 'lib/cms/links';
import {
  IComponentDoctorGalleryFields,
  IComponentGreenhouseFields,
  IComponentInsuranceListFields,
  IComponentPhotoGalleryFields,
  IComponentReviewScoreFields,
  IComponentReviewTestimonialListFields,
  IMetaLocationsSharedDataFields,
  IPageDoctorFields,
  IComponentMapFields,
  IPageLocationFields,
  IComponentDoctorCarouselFields,
  IPageDoctor,
  IComponentDoctorFinderWithOpenMapFields,
  IComponentProductCarouselFields,
  IProductVariantFields,
  IProductVariant,
  IComponentAcceptedInsuranceFields,
  IComponentSlimTextLeftImageRightFields,
  IComponentDoctorFinderFields,
} from 'types/contentful';
import { getReviewAverage, getReviewsWithComments, reviewFragmentToRequest } from 'lib/reviewinc';
import reviewIncClient from '../lib/reviewinc/client';
import { validateAgainstSchema } from 'lib/cms/validate';
import { doctorDisplayName, removeIfUndefined } from 'lib/util';
import cmsClient, { getAllEntries } from 'lib/cms/client';
import { ReviewScoreProps } from './Contentful/ReviewScore';
import boardClient from 'lib/greenhouse/client';
import { getJobList } from 'lib/greenhouse/api';
import { LOCATION_SELECT_FIELDS } from 'lib/consts';
import type { EntryCollection } from 'contentful';
import { DoctorFinderWithOpenMapProps, UserLocation } from './UI/DoctorFinderWithMap';
import { SlimHeroProps } from './Contentful/Hero/SlimHero';
import { ServerResponse, IncomingMessage } from 'http';
import { generateSSCookiesAndHeaders, SearchResultWithVariants, searchUrl } from 'lib/searchspring';
import { search } from 'lib/searchspring';
import { buildFullUrl } from 'lib/routing';
import { DoctorFinderProps } from './Contentful/DoctorFinder';
import { PlaceType } from './UI/NewLocationAutocomplete';
import { getPlace } from 'lib/freshpaint/api';
import { buildProductUrl } from 'lib/product';

/**
 * A dynamic component is defined as a React component and
 * a function that transforms from server-side props to
 * client-side props.
 *
 * The prepare function is used when an API call or some other
 * complex operation is needed to translate from Contentful
 * fields to props that can be consumed by the React component.
 */
export interface ComponentDef<SSP, CP = SSP> {
  component: React.ComponentType<CP>;
  prepare: (props: SSP, preview: boolean, otherParams?: otherParams) => Promise<CP>;
}

export interface otherParams {
  query: NodeJS.Dict<string | string[]>;
  req: IncomingMessage;
  res: ServerResponse;
  resolvedUrl: string;
  searchspringSiteId: string;
  freshPaintToken?: string;
}

export interface ProductVariantFieldsWithBrand extends IProductVariantFields {
  brand?: string | null;
  url?: string;
}

/**
 * Define a dynamic component that doesn't require server-side
 * translation.
 */
function def<P>(comp: React.ComponentType<P>): ComponentDef<P> {
  return {
    component: comp,
    prepare: (props: P) => Promise.resolve(props),
  };
}

/**
 * Define a dynamic component that requires server-side translation.
 * A function is provided to translate from the server-side props (SSP)
 * to the client-side props (CP).
 */
function serverSideDef<SSP, CP>(
  comp: React.ComponentType<CP>,
  prepare: (props: SSP, preview: boolean, otherParams?: otherParams) => Promise<CP>
): ComponentDef<SSP, CP> {
  return {
    component: comp,
    prepare: prepare,
  };
}

/**
 * A mapping of Contentful content types to the component definitions.  'dynamic` is
 * used to lazy load only the React components that are needed for a particular page.
 */
const components = {
  componentImageLeftTextRight: def(dynamic(() => import('./Contentful/LeftImageRightText'))),
  componentTextLeftImageRight: def(dynamic(() => import('./Contentful/RightImageLeftText'))),
  componentImageTextOffset: def(dynamic(() => import('./Contentful/ImageTextOffset'))),
  componentSimpleHeroImageLeftTextRight: def(
    dynamic(() => import('./Contentful/Hero/SimpleHeroLeftImageRightText'))
  ),
  componentSimpleHeroImageRightTextLeft: def(
    dynamic(() => import('./Contentful/Hero/SimpleHeroRightImageLeftText'))
  ),
  componentLeftImageRightAccordion: def(
    dynamic(() => import('./Contentful/ImageLeftAccordionRight'))
  ),
  componentImageWithTitle: def(dynamic(() => import('./Contentful/ImageWithTitle'))),
  componentTestimonialList: def(dynamic(() => import('./Contentful/Testimonials'))),
  componentTextOverThreeBlocks: def(dynamic(() => import('./Contentful/TextOverThreeBlocks'))),
  componentArticleCarousel: def(dynamic(() => import('./Contentful/ArticleCarousel'))),
  componentVideoCarousel: def(dynamic(() => import('./Contentful/VideoCarousel'))),
  componentSideBySide: def(dynamic(() => import('./Contentful/SideBySideBlock'))),
  componentVideoPlayer: def(dynamic(() => import('./Contentful/VideoPlayer'))),
  componentHeroPrimaryCircle: def(dynamic(() => import('./Contentful/Hero/HeroPrimaryCircle'))),
  componentHeroImageWithTextOverlay: def(
    dynamic(() => import('./Contentful/Hero/HeroImageWithTextOverlay'))
  ),
  componentQuestionAndAnswer: def(dynamic(() => import('./Contentful/QuestionAndAnswer'))),
  componentFrequentlyAskedQuestions: def(
    dynamic(() => import('./Contentful/FrequentlyAskedQuestions'))
  ),
  componentHeroInlineCircle: def(dynamic(() => import('./Contentful/Hero/HeroInlineCircle'))),
  componentHeroFadingImage: def(dynamic(() => import('./Contentful/Hero/HeroFadingImage'))),
  componentSlimTextLeftImageRight: serverSideDef(
    dynamic(() => import('./Contentful/Hero/SlimHero')),
    async ({
      heading,
      body,
      image,
      mobileImage,
      cta,
    }: IComponentSlimTextLeftImageRightFields): Promise<SlimHeroProps> => {
      return {
        image: image ? image.fields.file.url : './image/CTA-1.jpg',
        mobileImage: mobileImage?.fields.file.url ?? null,
        cta: cta?.fields,
        heading: heading,
        body: body,
      };
    }
  ),
  componentCarouselReviewList: def(dynamic(() => import('./Contentful/ReviewCarousel'))),
  componentLocationServiceOfferings: def(dynamic(() => import('./UI/LocationOffering'))),
  componentAcceptedInsurance: serverSideDef(
    dynamic(() => import('./UI/NewInsuranceProviders')),
    async (props: IComponentAcceptedInsuranceFields) => ({
      ...props,
      cta: props.cta.fields,
    })
  ),
  componentLinkList: def(dynamic(() => import('./Contentful/LinkList'))),
  componentForm: def(dynamic(() => import('./Contentful/Form'))),
  componentDoctorFinder: serverSideDef(
    dynamic(() => import('./Contentful/DoctorFinder')),
    async (
      props: IComponentDoctorFinderFields,
      preview: boolean,
      otherParams?: otherParams
    ): Promise<DoctorFinderProps> => {
      return {
        ...props,
        freshPaintToken: otherParams?.freshPaintToken || '',
      };
    }
  ),
  componentDoctorFinderWithOpenMap: serverSideDef(
    dynamic(() => import('./UI/DoctorFinderWithMap')),
    async (
      props: IComponentDoctorFinderWithOpenMapFields,
      preview: boolean,
      otherParams?: otherParams
    ): Promise<DoctorFinderWithOpenMapProps> => {
      const locationsPageData = await getAllEntries<IPageLocationFields>(cmsClient(preview), {
        'fields.site.sys.contentType.sys.id': 'site',
        'fields.site.fields.id': props.site?.fields.id as string,
        content_type: 'pageLocation',
        include: 1,
      });

      const sharedLocationData = await getAllEntries<IMetaLocationsSharedDataFields>(
        cmsClient(preview),
        {
          'fields.site.sys.contentType.sys.id': 'site',
          'fields.site.fields.id': props.site?.fields.id as string,
          content_type: 'metaLocationsSharedData',
          include: 1,
        }
      );

      const placeId = otherParams?.query['placeId'] as string;
      const freshPaintToken = otherParams?.freshPaintToken || '';
      let defaultUserLocation: UserLocation | null = null;
      if (placeId) {
        const place: PlaceType | null = await getPlace(placeId, freshPaintToken);
        if (place) {
          defaultUserLocation = {
            map: {
              lat: place.lat,
              lng: place.lng,
            },
            place: place,
          };
        }
      }

      const cleanedLocationsPageData = locationsPageData.map((location) => {
        if (Array.isArray(location.fields.doctors)) {
          location.fields.doctors.forEach((doctor: IPageDoctor) => {
            delete doctor.fields.primaryLocation;
            delete doctor.fields.secondaryLocations;
          });
        }
        return location;
      });

      return {
        ...props,
        locations: cleanedLocationsPageData,
        markerPinIcon: sharedLocationData[0].fields.markerPinIcon ?? 'none',
        hideEyeIcon: sharedLocationData[0].fields.hideEyeIcon ?? false,
        defaultUserLocation: defaultUserLocation,
        locationBanners: sharedLocationData[0].fields.banners || [],
        showClosestClinicLocations:
          sharedLocationData[0].fields.showClosestClinicLocations ?? false,
        locationSearchRadius: sharedLocationData[0].fields.locationSearchRadius ?? 100,
      };
    }
  ),
  componentAssociateLocator: def(dynamic(() => import('./Contentful/AssociatesMap'))),
  componentPhotoGallery: serverSideDef(
    dynamic(() => import('./Contentful/PhotoGallery')),
    async (props: IComponentPhotoGalleryFields) => ({
      ...props,
      gallery: props.gallery.map((block) => block.fields),
    })
  ),
  componentGreenhouse: serverSideDef(
    dynamic(() => import('./Contentful/Greenhouse')),
    async (props: IComponentGreenhouseFields) => ({
      jobs: await getJobList(boardClient, props.greenhouseJobBoardId, props.brand),
      boardId: props.greenhouseJobBoardId,
    })
  ),
  componentTextOnly: def(dynamic(() => import('./Contentful/TextOnly'))),
  componentSimpleHeroTextOnly: def(dynamic(() => import('./Contentful/Hero/SimpleHeroTextOnly'))),
  componentJson: def(dynamic(() => import('./Contentful/JSONComponent'))),
  componentDropdownWithRedirect: def(dynamic(() => import('./Contentful/DropdownWithRedirect'))),
  componentThreeTextCallout: def(dynamic(() => import('./Contentful/TextCallouts'))),
  componentAccordian: def(dynamic(() => import('./UI/Accordion'))),
  componentReviewScore: serverSideDef(
    dynamic(() => import('./Contentful/ReviewScore')),
    (props: IComponentReviewScoreFields): Promise<ReviewScoreProps> =>
      getReviewAverage(reviewIncClient, reviewFragmentToRequest(props.reviewsIds)).then(
        (score) => ({
          ...removeIfUndefined({ ratingStatement: props.statement }),
          reviewCount: score.count,
          reviewOutOfFive: score.fiveStarAvg,
        })
      )
  ),
  componentReviewTestimonialList: serverSideDef(
    dynamic(() => import('./Contentful/ReviewTestimonialList')),
    ({ reviewsIds, title, backgroundColor }: IComponentReviewTestimonialListFields) =>
      getReviewsWithComments(reviewIncClient, reviewFragmentToRequest(reviewsIds)).then(
        (reviews) => ({
          reviews,
          ...removeIfUndefined({ title, backgroundColor }),
        })
      )
  ),
  componentInsuranceList: serverSideDef(
    dynamic(() => import('./UI/InsuranceProviders')),
    async (fields: IComponentInsuranceListFields, preview: boolean) => {
      const { insuranceProviders, insuranceDisclaimer, insuranceSlug } = (
        await getAllEntries<IMetaLocationsSharedDataFields>(cmsClient(preview), {
          'fields.site.sys.contentType.sys.id': 'site',
          'fields.site.fields.id': fields.site.fields.id,
          content_type: 'metaLocationsSharedData',
          include: 1,
        })
      )[0]?.fields || {
        insuranceProviders: [],
      };

      return {
        insuranceProviders: insuranceProviders.map((item) => item.fields),
        ...removeIfUndefined({
          insuranceSlug,
          insuranceDisclaimer,
        }),
      };
    }
  ),

  componentMap: serverSideDef(
    dynamic(() => import('./Contentful/Map')),
    async ({ bannerText, locations, defaultZoom }: IComponentMapFields, preview: boolean) => {
      const client = cmsClient(preview);

      const allEntries = await getAllEntries<IPageLocationFields>(client, {
        content_type: 'pageLocation',
        'sys.id[in]': locations.toString(),
        select: LOCATION_SELECT_FIELDS,
      });

      const sharedLocationData: EntryCollection<IMetaLocationsSharedDataFields> = await client.getEntries(
        {
          'fields.site.sys.contentType.sys.id': 'site',
          content_type: 'metaLocationsSharedData',
          include: 0,
          select: 'fields.hideEyeIcon',
        }
      );

      const sharedLocationDataFields = sharedLocationData.items[0]?.fields || {};
      const hideEyeIcon = !!sharedLocationDataFields.hideEyeIcon;

      //By Default first location will be taken as center
      const content = allEntries[0].fields;
      return {
        ...removeIfUndefined({ bannerText, defaultZoom }),
        content: content,
        hideEyeIcon: hideEyeIcon,
        nearbyLocations: allEntries,
      };
    }
  ),
  componentServicesGallery: def(dynamic(() => import('./Contentful/ServicesGallery'))),

  componentDoctorGallery: serverSideDef(
    dynamic(() => import('./Contentful/PhotoGallery')),
    async ({ title, site, typeOfDoctor }: IComponentDoctorGalleryFields, preview: boolean) => {
      const doctorEntries = await getAllEntries<IPageDoctorFields>(cmsClient(preview), {
        'fields.site.sys.contentType.sys.id': 'site',
        'fields.site.fields.id': site.fields.id,
        ...removeIfUndefined({ 'fields.typeOfDoctor': typeOfDoctor }),
        order: 'fields.lastName',
        content_type: 'pageDoctor',
        include: 1,
      });
      const gallery = doctorEntries.map(
        ({ fields: { slug, titles, firstName, lastName, doctorPhoto } }) => ({
          image: doctorPhoto,
          rowOneText: doctorDisplayName({ titles, firstName, lastName }),
          link: `doctors/${slug}`,
        })
      );

      return { heading: title, imagesPerRow: 5, gallery };
      ``;
    }
  ),
  componentDoctorCarousel: serverSideDef(
    dynamic(() => import('./Contentful/DoctorCarousel')),
    async ({ id, title, site, doctors }: IComponentDoctorCarouselFields) => {
      const doctorFields: IPageDoctorFields[] =
        doctors?.map((entry: IPageDoctor) => entry.fields) ?? [];

      const sanitizedData = doctorFields?.map((item) => {
        const sanitizedItem = { ...item };
        delete sanitizedItem.primaryLocation;
        delete sanitizedItem.secondaryLocations;
        return sanitizedItem;
      });
      return { id, title, doctors: sanitizedData, site };
    }
  ),
  componentProductCarousel: serverSideDef(
    dynamic(() => import('./Contentful/ProductCarousel')),
    async (
      { id, title, description, products, cta }: IComponentProductCarouselFields,
      preview: boolean,
      otherParams?: otherParams
    ) => {
      const skuFilter: string[] = [];
      const productFields: IProductVariantFields[] =
        products?.map((entry: IProductVariant) => {
          const fields = { ...entry.fields }; // Create a copy of fields
          skuFilter.push(`sku=${fields.sku}`); // collect filter
          delete fields.locationIds; // Remove locationIds from the copy
          return fields;
        }) ?? [];

      if (!otherParams) {
        return { id, title, description, products: productFields, cta: cta?.fields };
      }

      const headers = generateSSCookiesAndHeaders(otherParams.req, otherParams.res);
      const searchSummary = await search(
        searchUrl(
          otherParams.query,
          skuFilter,
          buildFullUrl(otherParams.req, otherParams.resolvedUrl)
        ),
        otherParams.searchspringSiteId,
        headers
      );

      const searchResults: SearchResultWithVariants[] = searchSummary.results;
      const productWithBrand: ProductVariantFieldsWithBrand[] =
        productFields?.map((item: IProductVariantFields) => {
          // Find matching search result by SKU and add name if found
          const newItem: ProductVariantFieldsWithBrand = {
            ...item,
            brand: null, // Initialize with null instead of undefined
          };
          const matchingResult = searchResults.find((result) => result?.sku.includes(item.sku));
          if (matchingResult) {
            const name = matchingResult.name;
            newItem.brand = matchingResult.brand || null; // Ensure it's never undefined
            const uid = matchingResult.uid;
            const firstVariantKey = Object.keys(matchingResult.variants)[0];
            const color = matchingResult.variants[firstVariantKey]?.color?.toLowerCase();

            if (color && uid && name) {
              newItem.url = buildProductUrl(name, color, uid, newItem.sku);
            }
          }
          return newItem;
        }) ?? [];

      return { id, title, description, products: productWithBrand, cta: cta?.fields };
    }
  ),
  componentRichContentCard: def(dynamic(() => import('./Contentful/RichContentCard'))),
  componentCTASection: def(dynamic(() => import('./Contentful/CTASection'))),
  componentMediaGallery: def(dynamic(() => import('./Contentful/MediaGallery'))),
  componentListItemWithIcon: def(dynamic(() => import('./Contentful/ListItemWithIcon'))),
  componentRelatedArticles: def(dynamic(() => import('./Contentful/RelatedArticles'))),
  componentImageAndRichContentColumn: def(
    dynamic(() => import('./Contentful/ImageAndRichContentColumn'))
  ),
  componentShareableRichContent: def(dynamic(() => import('./Contentful/ShareableRichContent'))),
  componentHeroFullVideo: def(dynamic(() => import('./Contentful/Hero/HeroFullVideo'))),
  componentJsScript: def(dynamic(() => import('./Contentful/JSScript'))),
  componentIFrame: def(dynamic(() => import('./Contentful/IFrame'))),
};

/**
 * A union type of all content types allowed for dynamic components.
 */
export type DynamicComponentTypes = keyof typeof components;

/**
 * A type that links the content type and a component definition types.
 */
export type GenericDef<
  K extends DynamicComponentTypes,
  SSP = unknown,
  CP = unknown
> = typeof components[K] & ComponentDef<SSP, CP>;

/**
 * A type guard that will cast a key to a DynamicComponentTypes if the key
 * is a content type used by a dynamic component.
 */
export function isDynamicComponent<K extends DynamicComponentTypes>(key: string): key is K {
  return Object.keys(components).includes(key);
}

/**
 * Returns the component definition for a content type.
 */
export function definitionForType<
  K extends DynamicComponentTypes,
  C extends GenericDef<K, SSP, CP>,
  SSP = unknown,
  CP = unknown
>(type: K): C {
  return components[type] as C;
}

const SITENAME_REPLACER = new RegExp(`{siteName}`, 'gi');

/**
 * stringify fields to find all references to {siteName}
 * token and replace with the current siteName
 */
function replaceSitePlaceholder<T>(fields: T, siteName: string): T {
  const stringValue = JSON.stringify(fields);
  const sanitizedSiteName = siteName.replace(/[^\w]+|\s+/g, ' ');
  const hydrated = stringValue.replace(SITENAME_REPLACER, sanitizedSiteName);
  return JSON.parse(hydrated);
}

/**
 * Return a React element respresenting the prepared dynamic component.  Site name placeholders
 * are replaced in the props, and an error component is returned instead if validation fails for
 * preview components.
 */
export function buildDynamicComponent<
  K extends DynamicComponentTypes,
  C extends GenericDef<K, unknown, CP>,
  CP
>(
  compObj: PreparedComponentObject<K, CP>,
  siteName: string,
  preview?: boolean
): React.ReactElement {
  const comp = definitionForType<K, C, unknown, CP>(compObj.type).component;

  const props = replaceSitePlaceholder(compObj.fields, siteName);

  if (preview) {
    const errors = validateAgainstSchema(compObj.type, props);
    if (errors) {
      return (
        <div className="rounded border-2 p-2 m-2 border-highlight">
          Failed to render component, validations failed:
          <ul>
            {errors.map((error, idx) => (
              <li key={idx}>- {error.message}</li>
            ))}
          </ul>
        </div>
      );
    }
  }

  return React.createElement(comp, props);
}

/**
 * A component that has been translated and is ready to be transformed into
 * a React element.
 */
export interface PreparedComponentObject<K extends DynamicComponentTypes, CP> {
  type: K;
  fields: CP;
}

/**
 * Returns a prepared component object by calling prepare on the related component defintion.
 */
export async function translateServerSideComponent<
  K extends DynamicComponentTypes,
  C extends GenericDef<K, SSP, CP>,
  SSP,
  CP
>(
  { type, fields }: DynamicComponentObject<K, C, SSP, CP>,
  preview: boolean,
  otherParams?: otherParams
): Promise<PreparedComponentObject<K, CP>> {
  const def = definitionForType<K, C, SSP, CP>(type);

  const newProp = await def.prepare(fields, preview, otherParams || undefined);

  return {
    type,
    fields: newProp,
  };
}
